import { useState } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Row, TextInput } from "@narmi/design_system";
import { currencyToCents } from "byzantine";
import Filters from "byzantine/src/filters";

import {
  LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY,
  LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY,
} from "../../constants";
import { useRangeFilter } from "../../hooks/useRangeFilter";

import styles from "./AmountFilterPanel.module.scss";

export interface AmountFilterPanelProps {
  close: () => void;
  remove: () => void;
  onApply: () => void;
}

export const parseNumber = (raw: string): number | undefined => {
  if (raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isNaN(n) ? undefined : n;
};

const getDisplayValue = (value: number | undefined): string => {
  if (value === undefined) return "";
  return Filters.currency(value as Cents);
};

const AmountFilterPanel = ({
  close,
  remove,
  onApply,
}: AmountFilterPanelProps) => {
  const { l10n } = useLocalization();
  const { min, max, setRange, clearRange } = useRangeFilter<number>({
    queryKeyMin: LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY,
    queryKeyMax: LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY,
    parseValue: parseNumber,
    serializeValue: String,
    formatLabel: () => undefined,
  });

  const [draftMin, setDraftMin] = useState<number | undefined>(min);
  const [draftMax, setDraftMax] = useState<number | undefined>(max);

  const hasError =
    typeof draftMin === "number" &&
    typeof draftMax === "number" &&
    draftMin > draftMax;

  const handleApply = () => {
    setRange(draftMin, draftMax);
    onApply();
    close();
  };

  const handleRemove = () => {
    clearRange();
    onApply();
    remove();
  };

  return (
    <div className={styles["amount-filter-panel"]}>
      <div className={styles["amount-filter-panel-inputs"]}>
        <TextInput
          label={l10n.getString("label-min-amount")}
          value={getDisplayValue(draftMin)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const cents = currencyToCents(e.target.value);
            setDraftMin(
              cents === 0 && e.target.value.trim() === "" ? undefined : cents
            );
          }}
          error={hasError ? l10n.getString("start-amount-error") : undefined}
          testId="ledger-amount-filter-min"
        />
        <TextInput
          label={l10n.getString("label-max-amount")}
          value={getDisplayValue(draftMax)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const cents = currencyToCents(e.target.value);
            setDraftMax(
              cents === 0 && e.target.value.trim() === "" ? undefined : cents
            );
          }}
          error={hasError ? l10n.getString("end-amount-error") : undefined}
          testId="ledger-amount-filter-max"
        />
      </div>
      <div className={styles["amount-filter-panel-divider"]} />
      <div className="padding--x--s padding--y--xs">
        <Row justifyContent="space-between" alignItems="center">
          <Button
            kind="plain"
            size="s"
            label={l10n.getString("live-ledger-filter-amount-remove")}
            onClick={handleRemove}
            type="button"
            testId="ledger-amount-filter-remove"
          />
          <Button
            kind="primary"
            size="s"
            label={l10n.getString("live-ledger-filter-amount-apply")}
            onClick={handleApply}
            type="button"
            disabled={hasError}
            testId="ledger-amount-filter-apply"
          />
        </Row>
      </div>
    </div>
  );
};

export default AmountFilterPanel;
